import React, { useEffect, useState, useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import Layout, { PageHeader } from "@/components/Layout";
import { BookOpen, DownloadSimple, MagnifyingGlass } from "@phosphor-icons/react";

const DOC_URL = "/SENTRYWATCH_TECH_GUIDE.md";

// Hoisted to module scope so React doesn't tear down/remount every markdown
// subtree on each parent render (fixes react/no-unstable-nested-components).
const MD_COMPONENTS = {
  h1: ({ children }) => (
    <h1 className="font-display text-3xl font-bold tracking-tight text-white mb-4 mt-0">{children}</h1>
  ),
  h2: ({ children }) => {
    const text = String(children?.[0] || children);
    const slug = text.toLowerCase().replace(/[^\w\s-]/g, "").replace(/\s+/g, "-");
    return (
      <h2 id={slug} className="font-display text-xl font-bold tracking-tight text-white mt-10 mb-3 pb-2 border-b border-border scroll-mt-6">
        {children}
      </h2>
    );
  },
  h3: ({ children }) => (
    <h3 className="font-display text-base font-bold tracking-tight text-brand-cyan mt-6 mb-2">{children}</h3>
  ),
  p: ({ children }) => <p className="text-sm text-slate-300 leading-relaxed mb-3">{children}</p>,
  ul: ({ children }) => <ul className="text-sm text-slate-300 list-disc pl-6 mb-3 space-y-1">{children}</ul>,
  ol: ({ children }) => <ol className="text-sm text-slate-300 list-decimal pl-6 mb-3 space-y-1">{children}</ol>,
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  code: ({ inline, children }) =>
    inline
      ? <code className="text-[12px] bg-brand-cyan/10 text-brand-cyan px-1.5 py-0.5 rounded">{children}</code>
      : <code className="block font-mono text-xs bg-background-deep border border-border rounded p-3 overflow-x-auto text-slate-200">{children}</code>,
  pre: ({ children }) => <pre className="mb-3">{children}</pre>,
  blockquote: ({ children }) => (
    <blockquote className="border-l-2 border-brand-cyan pl-3 italic text-slate-300 mb-3">{children}</blockquote>
  ),
  a: ({ href, children }) => (
    <a href={href}
      target={href?.startsWith("http") ? "_blank" : undefined}
      rel={href?.startsWith("http") ? "noopener noreferrer" : undefined}
      className="text-brand-cyan hover:text-white underline underline-offset-2">
      {children}
    </a>
  ),
  table: ({ children }) => (
    <div className="overflow-x-auto my-4 sw-card">
      <table className="w-full text-xs">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-background-deep/60">{children}</thead>,
  th: ({ children }) => (
    <th className="text-left py-2 px-3 text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-400 border-b border-border">{children}</th>
  ),
  td: ({ children }) => <td className="py-2 px-3 text-slate-200 border-b border-border align-top">{children}</td>,
  hr: () => <hr className="border-border my-8" />,
  strong: ({ children }) => <strong className="text-white font-semibold">{children}</strong>,
};

export default function HelpDocs() {
  const [md, setMd] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(DOC_URL, { cache: "no-cache" });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        setMd(await r.text());
      } catch (e) { setError(e.message); }
      finally { setLoading(false); }
    })();
  }, []);

  // Build sidebar TOC from the level-2 headers
  const toc = useMemo(() => {
    const out = [];
    const re = /^##\s+(.+)$/gm;
    let m;
    while ((m = re.exec(md)) !== null) {
      const title = m[1].trim();
      const slug = title.toLowerCase()
        .replace(/[^\w\s-]/g, "")
        .replace(/\s+/g, "-");
      out.push({ title, slug });
    }
    return out;
  }, [md]);

  const filtered = useMemo(() => {
    if (!query) return md;
    const lines = md.split("\n");
    // keep each H2 section that contains the query
    const sections = [];
    let buf = [];
    let keep = false;
    const q = query.toLowerCase();
    for (const line of lines) {
      if (line.startsWith("## ")) {
        if (keep && buf.length) sections.push(buf.join("\n"));
        buf = [line];
        keep = line.toLowerCase().includes(q);
      } else {
        buf.push(line);
        if (line.toLowerCase().includes(q)) keep = true;
      }
    }
    if (keep && buf.length) sections.push(buf.join("\n"));
    return sections.length ? sections.join("\n\n---\n\n") : `_No matches for **"${query}"**_`;
  }, [md, query]);

  return (
    <Layout>
      <PageHeader
        title="Tech Guide"
        subtitle="Step-by-step playbook for SentryIT technicians · v5"
        actions={
          <a href={DOC_URL} download="SentryWatch-Tech-Guide.md"
            data-testid="docs-download-btn"
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm border border-border hover:border-brand-cyan/40 hover:bg-brand-cyan/10 rounded-md transition-colors">
            <DownloadSimple size={14} weight="bold" /> Download .md
          </a>
        }
      />
      <div className="p-6" data-testid="docs-page">
        {loading ? (
          <div className="text-sm text-slate-500">loading guide…</div>
        ) : error ? (
          <div className="text-sm text-red-400">Failed to load: {error}</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-8 max-w-7xl">
            {/* TOC */}
            <aside className="lg:sticky lg:top-6 lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto">
              <div className="relative mb-3">
                <MagnifyingGlass size={12} weight="bold"
                  className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
                <input value={query} onChange={(e) => setQuery(e.target.value)}
                  placeholder="search…"
                  data-testid="docs-search-input"
                  className="w-full bg-background-deep border border-border rounded pl-7 pr-2 py-1.5 text-xs text-slate-200 focus:outline-none focus:border-brand-cyan" />
              </div>
              <nav className="space-y-0.5" data-testid="docs-toc">
                {toc.map(t => (
                  <a key={t.slug} href={`#${t.slug}`}
                    data-testid={`docs-toc-${t.slug}`}
                    className="block text-xs text-slate-400 hover:text-brand-cyan py-1 truncate">
                    {t.title}
                  </a>
                ))}
              </nav>
              <div className="mt-6 pt-4 border-t border-border">
                <div className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500 mb-1.5">
                  <BookOpen size={11} weight="duotone" className="inline mr-1 text-brand-cyan" /> Tip
                </div>
                <p className="text-[11px] text-slate-400 leading-relaxed">
                  Bookmark this page. Use search above to jump straight to a topic.
                </p>
              </div>
            </aside>

            {/* Content */}
            <article className="sw-prose" data-testid="docs-content">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={MD_COMPONENTS}
              >
                {filtered}
              </ReactMarkdown>
            </article>
          </div>
        )}
      </div>
    </Layout>
  );
}
